import React from 'react';
import { Tooltip } from '@radix-ui/themes';
import { TOOLTIPS, type TooltipKey } from '../../tooltips/copy';

export interface InfoTipProps {
  /**
   * Key into the TOOLTIPS map. Drives title + body.
   */
  tooltipKey: TooltipKey;
  /**
   * When provided, wraps these children — hover/focus on the children shows
   * the tooltip. When omitted, renders a small ⓘ icon as the trigger.
   */
  children?: React.ReactNode;
  /**
   * Positioning hint for Radix. Defaults to 'top'.
   */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * Optional wrapper class for inline usage alongside an existing label.
   */
  className?: string;
}

/**
 * Information tooltip. Either wraps an existing element or renders a small
 * info-icon trigger. Content comes from the shared TOOLTIPS copy map so
 * product + design can edit copy without touching component code.
 *
 * Accessibility: Radix Tooltip handles both hover (mouse) and focus
 * (keyboard) triggers and renders the tooltip text via aria-describedby.
 * Touch devices (tablets) get a tap-and-hold fallback via Radix default.
 */
export const InfoTip: React.FC<InfoTipProps> = ({
  tooltipKey,
  children,
  side = 'top',
  className,
}) => {
  const copy = TOOLTIPS[tooltipKey];
  const content = (
    <div style={{ maxWidth: 280 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{copy.title}</div>
      <div style={{ fontSize: 13, lineHeight: 1.4 }}>{copy.body}</div>
    </div>
  );

  if (children) {
    return (
      <Tooltip content={content} side={side}>
        <span className={className} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'help' }}>
          {children}
        </span>
      </Tooltip>
    );
  }

  // Standalone info-icon trigger
  return (
    <Tooltip content={content} side={side}>
      <button
        type='button'
        aria-label={copy.title}
        className={className}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          // ~30px touch target (14px icon + 8px padding each side) while the
          // negative margin keeps the ~14px visual footprint, so inline layout
          // next to labels is unchanged. Improves tap accuracy on mobile.
          padding: 8,
          margin: -8,
          border: 'none',
          background: 'transparent',
          color: '#6D717F',
          cursor: 'help',
          verticalAlign: 'middle',
        }}
      >
        <svg
          width='14'
          height='14'
          viewBox='0 0 16 16'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          aria-hidden='true'
        >
          <circle cx='8' cy='8' r='7' stroke='currentColor' strokeWidth='1.25' />
          <circle cx='8' cy='4.75' r='0.9' fill='currentColor' />
          <path d='M8 7.5v4.75' stroke='currentColor' strokeWidth='1.25' strokeLinecap='round' />
        </svg>
      </button>
    </Tooltip>
  );
};
